/**
 * Die Tagesaufnahme schweigt, solange etwas klingt. (05.09.2026)
 *
 * ## Woher das kommt
 *
 * Martin, zweimal: *„Wenn er etwas vorliest, dann läuft der Rekorder
 * trotzdem weiter. Optimal wäre, wenn eine Wiedergabe stattfindet, dass der
 * Rekorder pausiert."* — und einen Tag später, als es immer noch lief:
 * *„Übrigens, er hat gerade wieder was vorgelesen und der Aufnahmeplayer ist
 * immer noch weitergelaufen."*
 *
 * ## Warum der erste Versuch nicht griff
 *
 * Ich hatte die Pause im **nativen** Teil eingebaut, in `enterPlaybackHiFi`.
 * Diese Funktion wird an **genau einer** Stelle gerufen
 * (`PawbotRecorderPlugin.swift:91`) — dem Hi-Fi-Weg für Bluetooth. Vorlesen
 * läuft aber im WKWebView über ein `<audio>`-Element und sagt dem Nativen
 * kein Wort. Die Pause konnte also nie greifen; sie hing am falschen Ort.
 *
 * ## Der richtige Ort
 *
 * Der {@link ./ton-leitstand.js Ton-Leitstand} weiß als Einziger, ob gerade
 * etwas klingt — egal auf welchem der fünf Wege. Also hängt die Schonung
 * dort und nirgends sonst.
 *
 * ⚠️ **Idempotent und selbstheilend.** Bei jeder Änderung wird der SOLL-Zustand
 *    hergestellt, nicht ein Umschalter betätigt. Sonst bliebe die Aufnahme
 *    stumm, wenn zwei Quellen sich ablösen und die Meldungen sich kreuzen.
 *
 * ⚠️ **Der Anruf bleibt außen vor.** Dort regelt das Native die Tonbelegung
 *    schon selbst. Zwei Stellen, die dasselbe entscheiden, sind genau der
 *    Fehler, den diese Datei behebt — ihn hier neu einzubauen wäre absurd.
 */
import { beobachte, type TonStand } from "./ton-leitstand.js";
import { meldeMikroAnlass } from "./mikro-spur.js";
import {
  pauseDayRecorderForMic,
  resumeDayRecorderAfterMic,
} from "./native-recorder.js";

/** Was die Aufnahme zum Schweigen bringt. Anruf fehlt mit Absicht (s.o.). */
const SCHONT: ReadonlySet<string> = new Set(["spur", "vorlesen", "musik"]);

/** Ob wegen Ton pausiert wurde — damit nur zurückgenommen wird, was von hier kam. */
let wegenTon = false;
/*
 * ⚠️ 20.09.2026 — DER FEHLGRIFF WURDE JE GESPROCHENEM STUECK WIEDERHOLT.
 *
 *    Sichtbar erst, seit die Schonung sich meldet (pre.3083). Im Protokoll
 *    um 21:13, sechsmal in 53 Sekunden:
 *
 *        schonung-pause art=antwort
 *        schonung-pause-verfehlt
 *
 *    Der Grund war harmlos — es lief gar keine Tagesaufnahme, die man haette
 *    pausieren koennen (sie endete um 21:01). Die FOLGE war es nicht: bei
 *    einem Fehlgriff sprang `wegenTon` zurueck auf false, und `tts-audio-start`
 *    feuert pro STUECK. Also versuchte es jedes Stueck erneut — ein Sturm
 *    nativer Aufrufe mitten im Vorlesen, genau dort, wo das Mikro am selben
 *    Abend 1,5 s brauchte (`kalt=tts-start-hifi`).
 *
 *    Die ABSICHT haelt jetzt ueber die ganze Hoer-Sitzung, der GRIFF wird
 *    hoechstens alle 15 Sekunden neu versucht. Der Nachfass bleibt noetig:
 *    faengt die Tagesaufnahme mitten im Vorlesen an, soll sie trotzdem
 *    schweigen.
 */
let willSchweigen = false;
let letzterVersuch = 0;
/** So selten darf ein gescheiterter Griff wiederholt werden. */
export const GRIFF_WIEDERHOLUNG_MS = 15_000;
/*
 * ⚠️⚠️ 15.09.2026 — DIE HÖR-SITZUNG IST DER BESITZER, NICHT DER EINZELNE TON.
 *
 * Martin: *„Nach jeder Unterbrechung bleibt die Pause und es schaltet nicht
 * mehr auf Rekord. Und ich kann auch da nicht auf Rekord schalten."*
 *
 * Im Journal (15.09., 12:00-13:10) das Muster, 25-mal an einem Kapitel:
 * Bei JEDEM Abschnittswechsel räumt der Spieler die Engine ab
 * (`engine.destroy()` → Bühne frei → hier: nichts klingt → FORTSETZEN) und
 * betritt sie Millisekunden später neu (→ klingt → PAUSE). Das Fortsetzen
 * ist aber kein Schalter: es löst alle Wiedergabe-Halte, schaltet Hi-Fi ab,
 * ruft den nativen Rekorder — und dessen Rettung reißt nach drei Anläufen
 * die Sitzung ab („fortsetzen-halt:nichts-offen", „neubeginn-ohne-aufnahme:
 * idle"). Die neue Sitzung wird vom nächsten Abschnitt sofort wieder
 * pausiert (1,7 s → verworfen). Am Ende: Pause, die niemand mehr löst.
 *
 * Deshalb: Verstummt die Bühne, wird NICHT sofort fortgesetzt, sondern nach
 * einem Nachlauf — kehrt in dieser Zeit ein Ton zurück (nächster Abschnitt,
 * nächstes Stück), bleibt die Aufnahme einfach pausiert. Ein Abschnittswechsel
 * ist kein Ende der Hör-Sitzung.
 */
export const SCHONUNG_NACHLAUF_MS = 4000;
let nachlauf: ReturnType<typeof setTimeout> | null = null;

/*
 * ⚠️⚠️ 22.09.2026 (Ticket 2253) — EINUNDZWANZIG PAUSEN OHNE RUECKKEHR.
 *
 * Bilanz des 21.09. aus dem Journal, zwanzig Stunden:
 *
 *     51  schonung-pause          (Absicht zu pausieren)
 *     22  schonung-pause-verfehlt (der Griff ging ins Leere — kein Schaden)
 *      8  schonung-fortsetzen     (die Aufnahme kam zurueck)
 *
 * Bleiben 29 echte Pausen, von denen 8 zurueckkamen. Einundzwanzig nicht.
 * Dazu passt die Messung des Pruefstands derselben Nacht: die Probe „Die
 * Aufnahme kommt vollstaendig beim Server an" steht auf 32,9 % (rot ab 75),
 * und eine Sitzung meldet „134 min Wanduhr, nur 44 min Ton angekommen".
 *
 * Das Fortsetzen haengt an EINEM Nachlauf-Timer, und der wird nur von einem
 * Ereignis neu gestellt. Bleibt das letzte Ereignis aus — ein Tonende, das
 * nie gemeldet wird, eine Buehne, die auf „klingt" stehen bleibt —, bleibt
 * die Aufnahme fuer immer pausiert, und niemand erfaehrt davon.
 *
 * ⚠️ Der Wecker RAET NICHT ueber die Ursache. Er stellt fest, dass eine
 *    Pause zu lange dauert, meldet es mit Dauer und Grund, und holt die
 *    Aufnahme zurueck, wenn nichts mehr klingt. Klingt doch noch etwas,
 *    meldet er nur — eine Wiedergabe, die eine Minute lang pausiert haelt,
 *    ist ebenfalls ein Befund.
 */
export const SCHONUNG_WECKER_MS = 60_000;
/*
 * ⚠️ DIE NOTFRIST GILT NUR DEM HAENGENDEN FLAG, NICHT DEM ECHTEN TON.
 *
 * Es gibt zwei Quellen dafuer, dass etwas klingt, und sie sind verschieden
 * verlaesslich:
 *
 *   - der LEITSTAND (`sollSchweigen(buehne)`) wird von echten
 *     Audio-Elementen gespeist und weiss als Einziger, ob wirklich etwas
 *     laeuft — ihm wird geglaubt, egal wie lange;
 *   - das FLAG `antwortSpricht` haengt an drei Ereignissen
 *     (`tts-audio-start` / `-end` / `-abbruch`). Genau diese Bauart ist im
 *     Haus schon dreimal haengengeblieben (15.09.: „Abbruch ist kein Ende",
 *     derselbe fehlende Hoerer in drei Modulen, Mikro 8-80 s kalt).
 *
 * Nach der Notfrist wird darum NUR dann eingegriffen, wenn allein das Flag
 * noch spricht und der Leitstand still ist. Ein Vorlesen ueber eine Stunde
 * bleibt unangetastet; eine Antwort, die seit fuenf Minuten „spricht",
 * spricht nicht mehr.
 *
 * Fuenf Minuten, weil der Reaper nach 25 Minuten Stille abschliesst
 * (REAPER_MINUTEN in stille-meldung.ts): frueh genug, um zwanzig Minuten zu
 * retten, spaet genug, um keine echte Antwort zu zerschneiden.
 */
export const SCHONUNG_NOTFRIST_MS = 5 * 60_000;
let wecker: ReturnType<typeof setTimeout> | null = null;
let pausiertSeit = 0;

function weckerAus(): void {
  if (wecker) {
    clearTimeout(wecker);
    wecker = null;
  }
  pausiertSeit = 0;
}

/**
 * Wie eine zu lange Pause heisst — eine Quelle fuer Meldung und Test.
 *
 * ⚠️ Die Dauer gehoert HINEIN. „haengt" allein sagt nicht, ob es um eine
 *    Minute oder um zwei Stunden geht, und genau das ist der Unterschied
 *    zwischen einem Schluckauf und einer verlorenen Aufnahme.
 */
export function weckerMeldung(
  dauerMs: number,
  klingtNoch: boolean,
  nurFlag = false,
): string {
  const sek = Math.max(1, Math.round(dauerMs / 1000));
  const grund = klingtNoch
    ? "klingt-noch"
    : nurFlag
      ? "stimme-haengt"
      : "nichts-klingt";
  return `schonung-haengt:${sek}s-${grund}`;
}

/**
 * Darf der Wecker die Aufnahme zurueckholen?
 *
 * Oeffentlich, damit die Abwaegung pruefbar ist statt im Timer zu stecken.
 */
export function darfZurueckholen(
  leitstandKlingt: boolean,
  antwortFlag: boolean,
  dauerMs: number,
): boolean {
  // Dem Leitstand wird geglaubt, egal wie lange es dauert.
  if (leitstandKlingt) return false;
  // Niemand spricht mehr: der Normalfall eines verlorenen Nachlaufs.
  if (!antwortFlag) return true;
  // Nur das Flag spricht noch — nach der Notfrist gilt es als haengend.
  return dauerMs >= SCHONUNG_NOTFRIST_MS;
}

/** Ein Aufruf nach dem anderen: sonst kreuzen sich Pause und Fortsetzen. */
let kette: Promise<unknown> = Promise.resolve();

function reihe(fn: () => Promise<unknown>): void {
  kette = kette.then(fn).catch(() => undefined);
}

/** Soll die Aufnahme jetzt schweigen? */
export function sollSchweigen(stand: TonStand | null): boolean {
  return Boolean(stand?.klingt && SCHONT.has(stand.art));
}

/**
 * Den Soll-Zustand herstellen. Öffentlich, damit Tests ihn ohne Leitstand
 * prüfen können.
 */
let buehne: TonStand | null = null;
let antwortSpricht = false;
/** Wann die Antwort zuletzt ein Stück begann (`tts-audio-start`) — 0 = noch nie. */
let letztesLebenszeichen = 0;

function neuAusrichten(): void {
  const soll = sollSchweigen(buehne) || antwortSpricht;
  if (soll) {
    // Ein Ton ist (wieder) da: ein laufender Nachlauf ist hinfällig.
    if (nachlauf) {
      clearTimeout(nachlauf);
      nachlauf = null;
    }
    if (wegenTon) return;
    const jetzt = Date.now();
    if (willSchweigen && jetzt - letzterVersuch < GRIFF_WIEDERHOLUNG_MS) return;
    const ersterVersuch = !willSchweigen;
    willSchweigen = true;
    letzterVersuch = jetzt;
    /*
     * ⚠️ 20.09.2026 — DIE PAUSE WAR UNSICHTBAR.
     *
     *    Martin: *„Jetzt fangen bei den Aufnahmen schon wieder diese
     *    verdammten Pausen an. Ich nehme auf und dann ploetzlich steht
     *    Pause dort."* — und im Journal stand dazu KEINE EINZIGE ZEILE.
     *    Die Pause geschieht im Frontend und im Nativen; der Server sieht
     *    nur die Folge (`tagesaufnahme=false` in den Sichtbarkeits-Zeilen),
     *    und auch die nur, wenn zufaellig ein Fensterwechsel dazwischenfaellt.
     *
     *    Genau diese Blindheit hat den Fehler zweimal wiederkehren lassen
     *    (05.09. und 15.09.). Eine Vermutung ueber etwas Unmessbares ist
     *    keine Diagnose. Ab jetzt steht jede Pause und jedes Fortsetzen im
     *    Protokoll — mit dem GRUND, denn „es pausiert" und „es pausiert
     *    wegen Musik" sind verschiedene Auskuenfte.
     */
    // ⚠️ Nur beim ERSTEN Versuch melden. Ein Melder, der je Stueck schreibt,
    //    ist selbst die Flut, die er beschreiben soll.
    if (ersterVersuch) {
      meldeMikroAnlass(
        "schonung-pause",
        `art=${buehne?.art ?? (antwortSpricht ? "antwort" : "?")}`,
      );
    }
    reihe(async () => {
      const griff = await pauseDayRecorderForMic("wiedergabe");
      wegenTon = Boolean(griff);
      if (!griff && ersterVersuch) {
        meldeMikroAnlass("schonung-pause-verfehlt", "nichts-zu-pausieren");
        return;
      }
      // Erst jetzt steht wirklich eine Aufnahme still — ab hier laeuft die Uhr.
      if (griff) stelleWecker();
    });
    return;
  }
  if (!willSchweigen || nachlauf) return;
  nachlauf = setTimeout(() => {
    nachlauf = null;
    // Noch einmal hinsehen: Ist inzwischen wieder etwas da, bleibt es dabei.
    if (sollSchweigen(buehne) || antwortSpricht) return;
    willSchweigen = false;
    // ⚠️ Nur fortsetzen, was wir wirklich angehalten haben. Ein Fortsetzen
    //    ohne Griff ist kein harmloser Leerlauf — es loest ALLE
    //    Wiedergabe-Halte und schaltet Hi-Fi ab (siehe 15.09.).
    if (!wegenTon) return;
    wegenTon = false;
    weckerAus();
    meldeMikroAnlass("schonung-fortsetzen", `nachlauf=${SCHONUNG_NACHLAUF_MS}ms`);
    reihe(async () => {
      await resumeDayRecorderAfterMic("wiedergabe");
    });
  }, SCHONUNG_NACHLAUF_MS);
}

/**
 * Den Wecker stellen: dauert die Pause zu lange, wird sie sichtbar — und
 * wenn nichts mehr klingt, auch beendet.
 *
 * ⚠️ Er ersetzt den Nachlauf NICHT. Der Nachlauf ist der Normalweg (vier
 *    Sekunden nach dem letzten Ton); der Wecker ist das Netz darunter, fuer
 *    den Fall, dass das letzte Ereignis nie kommt. Zwei Wege zum selben
 *    Ziel sind hier kein Widerspruch, sondern Absicht: der eine ist schnell,
 *    der andere gibt nie auf.
 *
 * ⚠️ Fortgesetzt wird nur, was von HIER pausiert wurde (`wegenTon`) — die
 *    Lehre vom 15.09.: ein Fortsetzen ohne Griff loest alle Wiedergabe-Halte
 *    und schaltet Hi-Fi ab.
 */
function stelleWecker(): void {
  stelleWeckerAb(Date.now());
}

/** Wie stelleWecker, aber die Uhr laeuft seit einem bekannten Zeitpunkt. */
function stelleWeckerAb(seit: number): void {
  if (wecker) return;
  pausiertSeit = seit;
  wecker = setTimeout(() => {
    wecker = null;
    if (!wegenTon) return;
    const dauer = pausiertSeit ? Date.now() - pausiertSeit : SCHONUNG_WECKER_MS;
    const leitstandKlingt = sollSchweigen(buehne);
    const nurFlag = !leitstandKlingt && antwortSpricht;
    /*
     * 16.10.2026 (Tickets 3295/3298): Die Notfrist zählt ab dem LETZTEN Lebenszeichen der Antwort, nicht ab der Pause.
     * Das Chat-Vorlesen steht nicht auf der Bühne (meldeAntwort, unten) — für den Leitstand klingt es nie. Bis heute
     * galt darum JEDE Antwort, die länger als fünf Minuten vorlas, als „hängendes Flag“: Journal 16.10., Konto 2,
     * `300s-stimme-haengt` um 04:58:24, 05:12:28, 05:19:30, 07:56:05, jedes Mal binnen einer Sekunde gefolgt von
     * `[tts-weiter] grund=pausiert … motor=unterbrochen` — das Fortsetzen der Tagesaufnahme hielt den laufenden Ton an.
     * Vorn fängt der Wiederanlauf das auf; hinten (Bildschirm aus) nicht: um 07:31:31 dieselbe Notbremse, ab 07:33:23
     * kein Stück mehr (Stück 29 von einer Antwort mit 10261 Zeichen), kein Ende, keine Meldung.
     * `tts-audio-start` kommt je Stück — eine Antwort, die noch Stücke beginnt, hängt nicht.
     */
    const seitLeben = antwortSpricht && letztesLebenszeichen ? Date.now() - letztesLebenszeichen : dauer;
    const frist = nurFlag ? Math.min(dauer, seitLeben) : dauer;
    meldeMikroAnlass(
      weckerMeldung(dauer, leitstandKlingt, nurFlag),
      nurFlag ? `wecker leben=${Math.round(seitLeben / 1000)}s` : "wecker",
    );
    if (!darfZurueckholen(leitstandKlingt, antwortSpricht, frist)) {
      /*
       * Noch nicht eingreifen — aber weiter hinsehen, sonst meldet sich der
       * Fall genau einmal und verschwindet dann.
       */
      const seit = pausiertSeit || Date.now();
      pausiertSeit = seit;
      wecker = setTimeout(() => {
        wecker = null;
        pausiertSeit = seit;
        stelleWeckerAb(seit);
      }, SCHONUNG_WECKER_MS);
      return;
    }
    // Die Aufnahme steht still, und niemand hat mehr ein Recht darauf.
    wegenTon = false;
    willSchweigen = false;
    // ⚠️ Das haengende Flag mit loesen, sonst pausiert der naechste Blick
    //    sofort wieder — und der Wecker liefe in einen Kreis.
    if (nurFlag) antwortSpricht = false;
    weckerAus();
    reihe(async () => {
      await resumeDayRecorderAfterMic("wiedergabe");
    });
  }, SCHONUNG_WECKER_MS);
}

/** Die Bühne des Leitstands: Spiegel, Alltag-Spur, Musik. */
export function richteAus(stand: TonStand | null): void {
  buehne = stand;
  neuAusrichten();
}

/**
 * Das Chat-Vorlesen. (05.09.2026, Martin: „sonst hört er sich ja selbst")
 *
 * Die gesprochene Antwort (tts-queue) steht NICHT auf der Bühne — sie soll
 * keine Abspielleiste zeigen. Bis heute hielt app.ts die Aufnahme dafür
 * selbst an (der zweite Besitzer, siehe pre.2612). Jetzt zählt sie hier
 * als zweite Quelle mit: EIN Grund („wiedergabe"), zwei Zuflüsse.
 */
export function meldeAntwort(spricht: boolean): void {
  antwortSpricht = spricht;
  // Jeder Stückbeginn ist ein Lebenszeichen — die Notfrist des Weckers zählt ab hier (Tickets 3295/3298).
  if (spricht) letztesLebenszeichen = Date.now();
  neuAusrichten();
}

export function starteRekorderSchonung(): void {
  beobachte(richteAus);
  // Start je Stück, Ende einmal beim Leerlaufen, Abbruch statt Ende bei
  // Unterbrechung — alle drei landen im selben Flag.
  window.addEventListener("voice-v8:tts-audio-start", () => meldeAntwort(true));
  window.addEventListener("voice-v8:tts-audio-end", () => meldeAntwort(false));
  window.addEventListener("voice-v8:tts-audio-abbruch", () =>
    meldeAntwort(false),
  );
}

export function _setzeSchonungZurueck(): void {
  wegenTon = false;
  willSchweigen = false;
  weckerAus();
  letzterVersuch = 0;
  buehne = null;
  antwortSpricht = false;
  letztesLebenszeichen = 0;
  kette = Promise.resolve();
  if (nachlauf) {
    clearTimeout(nachlauf);
    nachlauf = null;
  }
}
